export const getAllFeatureFlagsForEventSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
};

export const getFeatureFlagSchema = {
  params: {
    type: "object",
    properties: {
      flagId: { type: "number" },
    },
    required: ["flagId"],
  },
};

export const updateFeatureFlagSchema = {
  params: {
    type: "object",
    properties: {
      flagId: { type: "number" },
    },
    required: ["flagId"],
  },
  body: {
    type: "object",
    properties: {
      enabled: { type: "boolean" },
    },
    required: ["enabled"],
  },
};

// Feature flag constants
export const FEATURE_FLAGS = {
  REGISTRATIONS: "registrations",
  INVITE_EMAILS: "invite_emails",
  MULTILINGUAL: "multilingual",
  GIVEAWAYS: "giveaways",
  DOCUMENTS: "documents",
  BADGES: "badges",
  POLLS: "polls",
  SURVEYS: "surveys",
  ATTENDEE_INTERESTS_TAGS: "attendee_interests_tags",
  SESSION_RATINGS: "session_ratings",
  CHECK_IN: "check_in",
  EVENT_COMPLIANCE: "event_compliance",
};

// Feature flag metadata
export const FEATURE_FLAGS_METADATA = {
  [FEATURE_FLAGS.REGISTRATIONS]: {
    label: "Registration",
    description:
      "Branded sign-up pages, approvals, and ticket issuance for your event.",
  },
  [FEATURE_FLAGS.INVITE_EMAILS]: {
    label: "Invite Emails",
    description:
      "Import a guest list and send branded invite emails with tracked sign-up links.",
  },
  [FEATURE_FLAGS.MULTILINGUAL]: {
    label: "Multilingual",
    description: "Localize your event content across up to 10 languages.",
  },
  [FEATURE_FLAGS.GIVEAWAYS]: {
    label: "Giveaway",
    description: "Sponsor raffles tied to your event.",
  },
  [FEATURE_FLAGS.DOCUMENTS]: {
    label: "Documents",
    description: "Enable or disable documents for the event",
  },
  [FEATURE_FLAGS.BADGES]: {
    label: "Badges",
    description: "Design and store printable attendee badge templates.",
  },
  [FEATURE_FLAGS.POLLS]: {
    label: "Polls",
    description: "Live session polls for real-time feedback.",
  },
  [FEATURE_FLAGS.SURVEYS]: {
    label: "Surveys",
    description: "Post-event feedback surveys.",
  },
  [FEATURE_FLAGS.ATTENDEE_INTERESTS_TAGS]: {
    label: "Attendee Interest Tags",
    description:
      "Topic tags so attendees can show interests and improve networking.",
  },
  [FEATURE_FLAGS.SESSION_RATINGS]: {
    label: "Session Ratings",
    description:
      "Star ratings for sessions, visible to organizers after the event.",
  },
  [FEATURE_FLAGS.CHECK_IN]: {
    label: "QR-based check-in control",
    description:
      "On-site check-in stations to scan tickets and track attendance.",
  },
  [FEATURE_FLAGS.EVENT_COMPLIANCE]: {
    label: "Compliance gate",
    description:
      "Require attendees to accept the organizer's own terms before using the event.",
  },
};

export const FEATURE_PACKAGES = {
  EVENT_FOUNDATION: {
    key: "event_foundation",
    label: "Event Foundation",
    description:
      "Everything needed to run a professional digital event experience for in-person events.",
    flags: [
      FEATURE_FLAGS.MULTILINGUAL,
      FEATURE_FLAGS.SESSION_RATINGS,
      FEATURE_FLAGS.EVENT_COMPLIANCE,
    ],
    fields: [
      {
        name: "attendeeLimit",
        label: "Attendees",
        description: "Maximum number of attendees included in this package.",
        type: "number",
        placeholder: "Attendee limit",
        step: 100,
      },
    ],
  },

  REGISTRATION_PROMOTION: {
    key: "registration_promotion",
    label: "Registration & Promotion",
    description:
      "Collect registrations and invite the right audience with a simple, branded registration flow.",
    flags: [FEATURE_FLAGS.REGISTRATIONS, FEATURE_FLAGS.INVITE_EMAILS],
  },

  AUDIENCE_ENGAGEMENT: {
    key: "audience_engagement",
    label: "Audience Engagement",
    description:
      "Increase interaction and feedback by turning attendees into active participants, not just listeners.",
    flags: [
      FEATURE_FLAGS.POLLS,
      FEATURE_FLAGS.SURVEYS,
      FEATURE_FLAGS.GIVEAWAYS,
      FEATURE_FLAGS.ATTENDEE_INTERESTS_TAGS,
    ],
  },

  ONSITE_OPERATIONS: {
    key: "onsite_operations",
    label: "On-site Operations",
    description:
      "Ensure fast entry, clear identification, and smooth on-site attendee flow.",
    flags: [FEATURE_FLAGS.CHECK_IN, FEATURE_FLAGS.BADGES],
  },

  CERTIFICATES_RECOGNITION: {
    key: "certificates_recognition",
    label: "Certificates & Recognition",
    description:
      "Provide official confirmation of participation and access to event documents after the event.",
    flags: [FEATURE_FLAGS.DOCUMENTS],
  },

  EXTENDED_APP_AVAILABILITY: {
    key: "extended_app_availability",
    label: "Extended App Availability",
    description:
      "Keep the event app available for a longer period before or after the event, based on your event needs.",
    flags: [],
    fields: [
      {
        name: "autoCloseDays",
        label: "Auto-close after (days)",
        description:
          "Days the event app stays available after it is activated.",
        type: "number",
        placeholder: "Auto-close after (days)",
        step: 1,
      },
    ],
  },
};

// Schema for validating feature flag names
export const featureFlagNameSchema = {
  type: "string",
  enum: Object.values(FEATURE_FLAGS),
};

// Schema for validating feature flag metadata
export const featureFlagMetadataSchema = {
  type: "object",
  properties: {
    label: { type: "string", minLength: 1 },
    description: { type: "string" },
  },
  required: ["label", "description"],
  additionalProperties: false,
};
